import styled from "styled-components";

// Status color categories
const STATUS_CATEGORIES = {
  success: ["CONFIRMED", "예약", "입장", "외출", "퇴장", "사용안함", "받기가능", "사용가능", "결제완료"],
  warning: ["PAID", "부분취소", "배송준비중", "배송중", "주문접수", "배송준비"],
  muted: ["완료", "사용함", "배송완료", "다운완료", "사용완료"],
  danger: ["노쇼", "만료됨", "취소요청", "취소완료", "기간만료"],
};

export const getStatusColor = (status: string): string => {
  if (STATUS_CATEGORIES.success.includes(status)) return "#22c55e";
  if (STATUS_CATEGORIES.warning.includes(status)) return "#eab308";
  if (STATUS_CATEGORIES.muted.includes(status)) return "#888";
  if (STATUS_CATEGORIES.danger.includes(status)) return "#ef4444";
  return "#888";
};

// Item Card - used across all list views
export const ItemCard = styled.div<{ visible?: boolean }>`
  border-radius: 0.5rem;
  box-shadow: 1px 2px 6px 0px hsl(0deg 0% 64% / 22%);
  background-color: white;
  border: 1px solid #e5e7eb;
  padding: 10px 20px;
  opacity: ${(props) => (props.visible !== false ? "1" : "0.5")};
  margin-bottom: 12px;
`;

// Card variant for MyPage (rounded corners, more padding)
export const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

// Filter Badge for filter tabs
export const FilterBadge = styled.button<{ active?: boolean }>`
  padding: 8px 18px;
  border-radius: 100px;
  border: none;
  font-size: 13px;
  font-family: "Gmarket-M";
  background-color: ${(props) => (props.active ? "#f6475f" : "#eee")};
  color: ${(props) => (props.active ? "white" : "#666")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

export const FilterBadgeContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  padding: 0 4px;
`;

// Status Badge with dot indicator
export const StatusBadge = styled.div<{ status?: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => getStatusColor(props.status ?? "")};
`;

export const StatusDot = styled.span<{ status?: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => getStatusColor(props.status ?? "")};
`;

// Empty state container
export const EmptyState = styled.div<{ height?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${(props) => props.height || "200px"};
  color: #888;
  font-size: 14px;
`;

// Order info section (right side of card header)
export const OrderInfo = styled.div`
  text-align: right;
`;

export const OrderId = styled.div`
  font-size: 11px;
  color: #f6475f;
`;

export const OrderDate = styled.div`
  font-size: 12px;
  color: #888;
`;

// Product name
export const ProductName = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

// Date info row
export const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
`;

// Seat/option badge
export const SeatBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
`;

// Action buttons container
export const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`;

// Cancel/secondary button
export const CancelButton = styled.button`
  padding: 8px 16px;
  background: white;
  border: 1px solid #eab308;
  color: #eab308;
  border-radius: 6px;
  font-size: 13px;
  font-family: "Gmarket-M";
  cursor: pointer;

  &:hover {
    background: #fefce8;
  }
`;

// Detail/primary outline button
export const DetailButton = styled.button`
  padding: 8px 16px;
  background: white;
  border: 1px solid #333;
  color: #333;
  border-radius: 6px;
  font-size: 13px;
  font-family: "Gmarket-M";
  cursor: pointer;

  &:hover {
    background: #f9f9f9;
  }
`;

// Content area wrapper
export const ContentArea = styled.div`
  padding: 16px;
  background: #f8f8f8;
  min-height: calc(100vh - 200px);
  padding-bottom: 100px;
`;

// Tab Navigation
export const TabNavigation = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #eee;
  background: white;
  padding: 0 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Tab = styled.button<{ active?: boolean }>`
  flex: 1;
  min-width: 72px;
  padding: 14px 0;
  background: transparent;
  border: none;
  font-size: 14px;
  font-family: "Gmarket-M";
  white-space: nowrap;
  color: ${(props) => (props.active ? "#000" : "#aaa")};
  border-bottom: 2px solid ${(props) => (props.active ? "#f6475f" : "transparent")};
  cursor: pointer;
  transition: all 0.2s;

  @media (max-width: 480px) {
    flex: 0 0 auto;
    min-width: 86px;
    padding: 12px 10px;
    font-size: 13px;
  }
`;
